"use client";

import {
  isLightOnlyWebsitePath,
  isWebsiteThemedPath,
} from "@/lib/marketing-analytics/website-paths";
import { ld } from "@/lib/theme/light-dark";
import { WEBSITE_THEME_STORAGE_KEY } from "@/lib/theme/scoped-theme-bootstrap";
import { ScopedThemeShell, useScopedTheme } from "@/lib/theme/scoped-theme-shell";
import { Moon, Sun } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname, useSelectedLayoutSegment } from "next/navigation";
import { type ReactNode, useEffect, useState } from "react";
import "./website-theme.css";

/** Website pages and the staff login share one light/dark (light by default); other public pages pass through. */
export function WebsiteThemeShell({ children }: { readonly children: ReactNode }): ReactNode {
  const pathname = usePathname();
  // Docs host serves help at "/": URL looks like website. Two theme locks there loop forever.
  const helpCenter = useSelectedLayoutSegment() === "help";
  if (helpCenter || pathname === null || !isWebsiteThemedPath(pathname)) return children;
  return (
    // Marker frees the website from the public light scope; `contents` adds no box.
    <div className="website-theme-scope contents">
      <ScopedThemeShell
        storageKey={WEBSITE_THEME_STORAGE_KEY}
        forcedTheme={isLightOnlyWebsitePath(pathname) ? "light" : undefined}
      >
        {children}
      </ScopedThemeShell>
    </div>
  );
}

/** Sun/moon button like the help center's. Renders nothing outside website pages. */
export function WebsiteThemeToggle({ className }: { readonly className?: string }): ReactNode {
  const scoped = useScopedTheme();
  const t = useTranslations("marketing.websiteTheme");
  // Server renders light; the stored theme only exists in the browser, so the label follows after mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (scoped === null) return null;
  const dark = mounted && scoped.theme === "dark";
  return (
    <button
      type="button"
      onClick={scoped.toggle}
      aria-label={dark ? t("hell") : t("dunkel")}
      className={`mk-theme-toggle inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] transition-colors hover:bg-black/5 dark:hover:bg-white/10 ${className ?? ""}`}
      style={{ color: ld("rgba(11,15,25,0.62)", "rgba(250,250,250,0.72)") }}
    >
      <Sun className="mk-theme-sun h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
      <Moon className="mk-theme-moon h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
    </button>
  );
}
